import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Move, Users, TrendingUp, DollarSign, AlertTriangle, Save, X } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface Zone {
  id: string;
  name: string;
  type: 'workspace' | 'meeting' | 'collaboration' | 'amenity';
  capacity: number;
  currentOccupancy: number;
  area: number;
  costPerSqFt: number;
  utilization: number;
  adjacentZones: string[];
}

interface Department {
  id: string;
  name: string;
  headcount: number;
  currentSpace: number;
  projectedGrowth: number;
  collaborationScore: number;
  primaryZones: string[];
}

interface SimulationMetrics {
  capacityDelta: number;
  costImpact: number;
  utilizationScore: number;
  collaborationScore: number;
  congestionRisk: 'low' | 'medium' | 'high';
  recommendations: string[];
}

const SpacePlanning: React.FC = () => {
  // State for zones and departments
  const [zones, setZones] = useState<Zone[]>([
    {
      id: 'z1',
      name: 'Open Office North',
      type: 'workspace',
      capacity: 50,
      currentOccupancy: 35,
      area: 2000,
      costPerSqFt: 45,
      utilization: 70,
      adjacentZones: ['z2', 'z3']
    },
    {
      id: 'z2',
      name: 'Conference Center',
      type: 'meeting',
      capacity: 30,
      currentOccupancy: 15,
      area: 1000,
      costPerSqFt: 55,
      utilization: 50,
      adjacentZones: ['z1', 'z4']
    },
    {
      id: 'z3',
      name: 'Innovation Hub',
      type: 'collaboration',
      capacity: 25,
      currentOccupancy: 20,
      area: 800,
      costPerSqFt: 50,
      utilization: 80,
      adjacentZones: ['z1', 'z4']
    },
    {
      id: 'z4',
      name: 'Break Area',
      type: 'amenity',
      capacity: 20,
      currentOccupancy: 8,
      area: 600,
      costPerSqFt: 40,
      utilization: 40,
      adjacentZones: ['z2', 'z3']
    }
  ]);

  const [departments, setDepartments] = useState<Department[]>([
    {
      id: 'd1',
      name: 'Engineering',
      headcount: 30,
      currentSpace: 1200,
      projectedGrowth: 15,
      collaborationScore: 85,
      primaryZones: ['z1', 'z3']
    },
    {
      id: 'd2',
      name: 'Sales',
      headcount: 20,
      currentSpace: 800,
      projectedGrowth: 10,
      collaborationScore: 75,
      primaryZones: ['z1', 'z2']
    }
  ]);

  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [simulationMetrics, setSimulationMetrics] = useState<SimulationMetrics>({
    capacityDelta: 0,
    costImpact: 0,
    utilizationScore: 75,
    collaborationScore: 80,
    congestionRisk: 'low',
    recommendations: []
  });

  // Add new state for editing
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [editForm, setEditForm] = useState<Partial<Zone>>({});

  // Effect to update simulation metrics when zones change
  useEffect(() => {
    updateSimulationMetrics();
  }, [zones]);

  const updateSimulationMetrics = () => {
    // Calculate new metrics based on current zone configuration
    const metrics: SimulationMetrics = {
      capacityDelta: calculateCapacityDelta(),
      costImpact: calculateCostImpact(),
      utilizationScore: calculateUtilizationScore(),
      collaborationScore: calculateCollaborationScore(),
      congestionRisk: calculateCongestionRisk(),
      recommendations: generateRecommendations()
    };
    setSimulationMetrics(metrics);
  };

  // Simulation calculation functions
  const calculateCapacityDelta = () => {
    const totalCapacity = zones.reduce((sum, zone) => sum + zone.capacity, 0);
    const totalOccupancy = zones.reduce((sum, zone) => sum + zone.currentOccupancy, 0);
    return ((totalCapacity - totalOccupancy) / totalCapacity) * 100;
  };

  const calculateCostImpact = () => {
    return zones.reduce((total, zone) => total + (zone.area * zone.costPerSqFt), 0);
  };

  const calculateUtilizationScore = () => {
    return zones.reduce((sum, zone) => sum + zone.utilization, 0) / zones.length;
  };

  const calculateCollaborationScore = () => {
    return departments.reduce((sum, dept) => sum + dept.collaborationScore, 0) / departments.length;
  };

  const calculateCongestionRisk = (): 'low' | 'medium' | 'high' => {
    const avgUtilization = calculateUtilizationScore();
    if (avgUtilization > 85) return 'high';
    if (avgUtilization > 70) return 'medium';
    return 'low';
  };

  const generateRecommendations = (): string[] => {
    const recommendations: string[] = [];
    const avgUtilization = calculateUtilizationScore();

    if (avgUtilization < 60) {
      recommendations.push('Consider consolidating underutilized spaces');
    }
    if (avgUtilization > 80) {
      recommendations.push('Evaluate expansion opportunities in high-traffic areas');
    }

    // Add department-specific recommendations
    departments.forEach(dept => {
      if (dept.projectedGrowth > 10) {
        recommendations.push(`Plan for ${dept.name} department growth of ${dept.projectedGrowth}%`);
      }
    });

    return recommendations;
  };

  // Enhanced zone selection handler
  const handleZoneSelect = (zoneId: string) => {
    setSelectedZone(zoneId);
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      setEditingZone(zone);
      setEditForm(zone);
    }
  };

  const handleZoneUpdate = (zoneId: string, updates: Partial<Zone>) => {
    setZones(zones.map(zone => 
      zone.id === zoneId ? { ...zone, ...updates } : zone
    ));
  };

  // Form handling functions
  const handleInputChange = (field: keyof Zone, value: string | number) => {
    setEditForm(prev => ({
      ...prev,
      [field]: field === 'name' || field === 'type' ? value : Number(value)
    }));
  };

  const handleSaveChanges = () => {
    if (editingZone && editForm) {
      handleZoneUpdate(editingZone.id, editForm);
      setEditingZone(null);
      setEditForm({});
    }
  };

  const handleCancelEdit = () => {
    setEditingZone(null);
    setEditForm({});
    setSelectedZone(null);
  };

  const getCongestionColor = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Space Planning Simulator</h2>
        <div className="text-sm text-gray-500">
          Real-time updates based on sensor data
        </div>
      </div>

      {/* Simulation Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-2">
            <Users className="w-5 h-5 text-blue-500 mr-2" />
            <h3 className="font-semibold">Capacity Impact</h3>
          </div>
          <p className="text-2xl font-bold">{simulationMetrics.capacityDelta.toFixed(1)}%</p>
          <p className="text-sm text-gray-500">Available capacity</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-2">
            <DollarSign className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="font-semibold">Cost Impact</h3>
          </div>
          <p className="text-2xl font-bold">${simulationMetrics.costImpact.toLocaleString()}</p>
          <p className="text-sm text-gray-500">Monthly operating cost</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
            <h3 className="font-semibold">Congestion Risk</h3>
          </div>
          <p className={`text-2xl font-bold ${getCongestionColor(simulationMetrics.congestionRisk)}`}>
            {simulationMetrics.congestionRisk.charAt(0).toUpperCase() + simulationMetrics.congestionRisk.slice(1)}
          </p>
          <p className="text-sm text-gray-500">Based on current layout</p>
        </div>
      </div>

      {/* Enhanced Zone Management */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Zone Configuration</h3>
              <p className="text-sm text-gray-500">Click a zone to edit</p>
            </div>
            <div className="space-y-4">
              {zones.map(zone => (
                <div 
                  key={zone.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedZone === zone.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => handleZoneSelect(zone.id)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold">{zone.name}</h4>
                      <p className="text-sm text-gray-500">{zone.type}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{zone.currentOccupancy}/{zone.capacity}</p>
                      <p className="text-sm text-gray-500">{zone.utilization}% utilized</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Zone Editor Panel */}
        <div className="bg-white rounded-lg shadow p-4">
          {editingZone ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Edit Zone</h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zone Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={editForm.type || ''}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="workspace">Workspace</option>
                    <option value="meeting">Meeting</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="amenity">Amenity</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacity
                  </label>
                  <input
                    type="number"
                    value={editForm.capacity || ''}
                    onChange={(e) => handleInputChange('capacity', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Area (sq ft)
                  </label>
                  <input
                    type="number"
                    value={editForm.area || ''}
                    onChange={(e) => handleInputChange('area', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost per Sq Ft
                  </label>
                  <input
                    type="number"
                    value={editForm.costPerSqFt || ''}
                    onChange={(e) => handleInputChange('costPerSqFt', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Occupancy
                  </label>
                  <input
                    type="number"
                    value={editForm.currentOccupancy || ''}
                    onChange={(e) => handleInputChange('currentOccupancy', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <button
                  onClick={handleSaveChanges}
                  className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 flex items-center justify-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Select a zone to edit its configuration
            </div>
          )}
        </div>
      </div>

      {/* Recommendations and Actions */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
        <div className="space-y-4">
          {simulationMetrics.recommendations.map((recommendation, index) => (
            <div key={index} className="flex items-start space-x-2">
              <TrendingUp className="w-5 h-5 text-blue-500 mt-0.5" />
              <p className="text-sm">{recommendation}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Department Growth Projections */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Department Growth Projections</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={departments.map(dept => ({
              name: dept.name,
              current: dept.headcount,
              projected: dept.headcount * (1 + dept.projectedGrowth / 100)
            }))}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="current" stackId="1" stroke="#8884d8" fill="#8884d8" name="Current Headcount" />
            <Area type="monotone" dataKey="projected" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Projected Headcount" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SpacePlanning; 